import { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RouteName, LOCAL_STORAGE_KEYS } from '../../constants/Constants';
import storage from '../../services/storage';
import PublicVariables from '../../constants/PublicVariables';
import { Localize } from './Localize';
import './LeftPanel.scss';

const LP_COLLAPSED_KEY = 'lp_collapsed';

const ChevronIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="15 18 9 12 15 6" />
    </svg>
);

const LeftPanel = () => {
    const [collapsed, setCollapsed] = useState(() => {
        const isMobile = window.innerWidth <= 768;
        const isCollapsed = isMobile
            ? true
            : localStorage.getItem(LP_COLLAPSED_KEY) === 'true';
        // Set immediately (before first paint) to avoid logo flash
        if (!isCollapsed) document.body.classList.add('lp-expanded');
        return isCollapsed;
    });
    const [localisedValues, setLocalisedValues] = useState({});
    const [domainConfigTick, setDomainConfigTick] = useState(0);

    const { selectedlanguage } = useSelector((state) => state.language);
    const portalName = useSelector((state) => state.portal?.portalname);

    const location = useLocation();
    const focusPageHeadingRef = useRef(false);

    useEffect(() => {
        if (!focusPageHeadingRef.current) return;
        focusPageHeadingRef.current = false;
        const main = document.getElementById('main-content');
        const target = main?.querySelector('h1') || main;
        if (!target) return;
        if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
        target.focus({ preventScroll: true });
    }, [location.pathname]);

    const onNavClick = (path) => {
        focusPageHeadingRef.current = location.pathname !== path;
    };

    // Re-read domain config whenever api.js fires 'domainConfigUpdated'
    useEffect(() => {
        const handler = () => setDomainConfigTick((t) => t + 1);
        window.addEventListener('domainConfigUpdated', handler);
        return () => window.removeEventListener('domainConfigUpdated', handler);
    }, []);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const domainConfig = useMemo(() => storage.get(LOCAL_STORAGE_KEYS.DOMAIN_CONFIGURATION), [domainConfigTick]);

    const rawLogo = PublicVariables.companyLOGO || domainConfig?.domainLogo;
    const logoUrl = rawLogo ? process.env.REACT_APP_COM_URL + rawLogo : null;

    useEffect(() => {
        if (selectedlanguage?.id) {
            setLocalisedValues(Localize[selectedlanguage.id] || Localize['en_US']);
        }
    }, [selectedlanguage]);

    // Toggle body class so Header logo fades via CSS when sidebar opens/closes
    useEffect(() => {
        document.body.classList.toggle('lp-expanded', !collapsed);
        return () => document.body.classList.remove('lp-expanded');
    }, [collapsed]);

    const isActive = (paths) => {
        const pathsArr = Array.isArray(paths) ? paths : [paths];
        return pathsArr.some(p => {
            const base = p.split('/:')[0]; // strip dynamic segments like /:sessionId
            return location.pathname === base || location.pathname.startsWith(base + '/');
        });
    };

    const toggleCollapsed = () => {
        const newVal = !collapsed;
        setCollapsed(newVal);
        localStorage.setItem(LP_COLLAPSED_KEY, String(newVal));
    };

    // When open: center button inside 72px panel → (72-32)/2 = 20
    // When closed: at left:0 so CSS transform can half-hide it (half-circle tab effect)
    const toggleBtnLeft = collapsed ? 0 : 20;

    return (
        <>
            <div className={`lp${collapsed ? ' lp--collapsed' : ''}`}>
                <div className="lp__inner">
                    <div className="lp__logo">
                        {logoUrl
                            ? <img src={logoUrl} alt={portalName || localisedValues["company_logo"] || "Company logo"} style={{ width: '32px', height: '32px', objectFit: 'contain' }} />
                            : <span className="lp__logo-fallback" aria-hidden="true">B</span>
                        }
                    </div>
                    <nav className="lp__nav">
                        {(() => {
                            const sessionsActive = isActive([RouteName.SESSIONS, RouteName.SESSION_DETAILS]);
                            const contactsActive = isActive(RouteName.CONTACTS);
                            // Key-based remount on active-state toggle forces Chrome
                            // to rebuild its paint plan for this tooltip subtree,
                            // avoiding the SPA-navigation paint-cache bug where the
                            // newly-active nav button's paint layer stays above the
                            // tooltip on subsequent hovers.
                            return (
                                <ul className="lp__list">
                                    <li className="custom-tooltip center-tooltip" key={`showme-${sessionsActive}`}>
                                        <Link
                                            to={RouteName.SESSIONS}
                                            onClick={() => onNavClick(RouteName.SESSIONS)}
                                            className={`lp__btn${sessionsActive ? ' lp__btn--active' : ''}`}
                                            aria-current={sessionsActive ? 'page' : undefined}
                                        >
                                            <span className="btn-icon icon-video-icon" aria-hidden="true" />
                                            <span className="lp__label">ShowMe</span>
                                        </Link>
                                        <div className="custom-tooltip-content">
                                            <div className="custom-tooltip-in">
                                                <p>ShowMe</p>
                                            </div>
                                        </div>
                                    </li>
                                    <li className="custom-tooltip center-tooltip" key={`contacts-${contactsActive}`}>
                                        <Link
                                            to={RouteName.CONTACTS}
                                            onClick={() => onNavClick(RouteName.CONTACTS)}
                                            className={`lp__btn${contactsActive ? ' lp__btn--active' : ''}`}
                                            aria-current={contactsActive ? 'page' : undefined}
                                        >
                                            <span className="for-icon icon-address-book btn-icon" aria-hidden="true" />
                                            <span className="lp__label">{localisedValues['contacts']}</span>
                                        </Link>
                                        <div className="custom-tooltip-content">
                                            <div className="custom-tooltip-in">
                                                <p>{localisedValues['contacts']}</p>
                                            </div>
                                        </div>
                                    </li>
                                </ul>
                            );
                        })()}
                    </nav>
                </div>
            </div>

            {/* Backdrop for mobile — closes panel when tapping outside */}
            {!collapsed && (
                <div
                    className="lp__backdrop"
                    onClick={toggleCollapsed}
                    aria-hidden="true"
                />
            )}

            <div
                className={`lp__toggle-wrap custom-tooltip top-tooltip center-tooltip${collapsed ? ' lp__toggle-wrap--collapsed' : ''}`}
                style={{ left: `${toggleBtnLeft}px` }}
            >
                <button
                    className="lp__toggle"
                    onClick={toggleCollapsed}
                    aria-label={collapsed ? localisedValues['show_side_panel'] : localisedValues['hide_side_panel']}
                >
                    <ChevronIcon />
                </button>
                <div className="custom-tooltip-content">
                    <div className="custom-tooltip-in">
                        <p>{collapsed ? localisedValues['show_side_panel'] : localisedValues['hide_side_panel']}</p>
                    </div>
                </div>
            </div>
        </>
    );
};

export default LeftPanel;
